/**
 * Test on a record: a dry run of the builder's definition as it stands,
 * saved or not, against one record. The server plans each step (read only)
 * and says what it would do, along the path the record would take now:
 * each If / else says which way it would go, and a "wait until" that has not
 * happened yet is shown pending (what follows assumes it times out, which is
 * what happens if nothing changes). Nothing is changed.
 */
import { useState } from 'react';
import {
  Alert, Badge, Button, Card, Group, Loader, Select, Stack, Text, TextInput, ThemeIcon,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconCheck, IconFlag, IconFlask, IconHourglass, IconPlayerSkipForward } from '@tabler/icons-react';
import { useDeals } from '../../api/crm';
import { usePeople } from '../../api/people';
import {
  useTestWorkflow, type RecordType, type WorkflowGoal, type WorkflowStep, type WorkflowTestResult, type WorkflowTrigger,
} from '../../api/workflows';
import { TOKENS } from '../../theme';
import { useVocab, type Vocab } from '../../vocab';
import { recordWord, stepLabel } from './workflowModel';

function hint(v: Vocab, rt: RecordType): string {
  switch (rt) {
    case 'submission': return `The ${v.one('lead')}'s id, from its detail.`;
    case 'person': return `Search by name or email, or paste a key such as p123 from the ${v.one('person')}'s page address.`;
    case 'deal': return `Search by name, or paste the ${v.one('deal')}'s id.`;
    case 'task': return `The ${v.one('task')}'s id.`;
  }
}

/** Search people or deals by name; other records are picked by id. */
function RecordSearch({ rt, onPick }: { rt: RecordType; onPick: (id: string) => void }) {
  const v = useVocab();
  const [q, setQ] = useState('');
  const [debounced] = useDebouncedValue(q.trim(), 300);
  const people = usePeople({ q: debounced, page_size: 10 }, rt === 'person' && debounced.length > 1);
  const deals = useDeals({ q: debounced, page_size: 10 }, rt === 'deal' && debounced.length > 1);
  const data = rt === 'person'
    ? (people.data?.people ?? []).map((p) => ({ value: p.id, label: p.name || p.email || p.id }))
    : (deals.data?.deals ?? []).map((d) => ({ value: d.id, label: d.name }));
  return (
    <Select
      size="xs"
      label={`Find ${v.a(rt === 'person' ? 'person' : 'deal')}`}
      placeholder="Type a name"
      searchable
      searchValue={q}
      onSearchChange={setQ}
      data={data}
      filter={({ options }) => options}
      nothingFoundMessage={debounced.length > 1 ? 'Nothing found' : 'Type at least two letters'}
      onChange={(id) => id && onPick(id)}
      value={null}
      data-testid="test-record-search"
    />
  );
}

export function TestPanel({
  workflowId, rt, trigger, conditions, steps, goals, canManage,
}: {
  /** Null until the workflow is first saved. */
  workflowId: string | null;
  rt: RecordType;
  trigger: WorkflowTrigger;
  conditions: unknown;
  steps: WorkflowStep[];
  goals?: WorkflowGoal[];
  canManage: boolean;
}) {
  const v = useVocab();
  const test = useTestWorkflow();
  const [recordId, setRecordId] = useState('');
  const [result, setResult] = useState<WorkflowTestResult | null>(null);

  async function run() {
    if (!workflowId || !recordId.trim()) return;
    setResult(null);
    try {
      const r = await test.mutateAsync({
        id: workflowId,
        body: { record_type: rt, record_id: recordId.trim(), trigger, conditions, steps, goals: goals ?? null },
      });
      setResult(r);
    } catch {
      /* shown from test.error */
    }
  }

  return (
    <Card padding="sm" withBorder data-testid="test-panel">
      <Stack gap="sm">
        <Group gap="xs" wrap="nowrap">
          <ThemeIcon variant="light" radius="xl" size={28} color="gray"><IconFlask size={15} /></ThemeIcon>
          <div>
            <Text size="sm" fw={600}>Test on a record</Text>
            <Text size="xs" c="dimmed">What this would do, with your unsaved changes. Nothing is changed.</Text>
          </div>
        </Group>
        {!canManage ? (
          <Text size="sm" c="dimmed">{`Testing needs permission to manage ${v.many('workflow')}.`}</Text>
        ) : !workflowId ? (
          <Text size="sm" c="dimmed">{`Save the ${v.one('workflow')} once to test it.`}</Text>
        ) : (
          <>
            {(rt === 'person' || rt === 'deal') && <RecordSearch rt={rt} onPick={setRecordId} />}
            <TextInput
              size="xs"
              label={`${recordWord(v, rt).replace(/^./, (x) => x.toUpperCase())} id`}
              description={hint(v, rt)}
              value={recordId}
              onChange={(e) => setRecordId(e.currentTarget.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') run(); }}
              data-testid="test-record-id"
            />
            <Button
              size="xs"
              variant="light"
              onClick={run}
              loading={test.isPending}
              disabled={!recordId.trim()}
              style={{ alignSelf: 'flex-start' }}
            >
              Run the test
            </Button>
          </>
        )}
        {test.error && <Alert color="red" variant="light" p="xs"><Text size="sm">{test.error.message}</Text></Alert>}
        {test.isPending && <Group justify="center"><Loader size="sm" /></Group>}
        {result && <TestResult result={result} />}
      </Stack>
    </Card>
  );
}

function TestResult({ result }: { result: WorkflowTestResult }) {
  const v = useVocab();
  const met = result.conditions_met;
  return (
    <Stack gap="xs" data-testid="test-result">
      <Group gap="xs" wrap="wrap">
        <Text size="sm" fw={600} style={{ overflowWrap: 'anywhere' }}>{result.record_name || result.record_id}</Text>
        {result.conditions_error ? (
          <Badge color="red" variant="light" radius="sm" tt="none">Conditions could not be checked</Badge>
        ) : met ? (
          <Badge color="chartGreen" variant="light" radius="sm" tt="none">Conditions met</Badge>
        ) : (
          <Badge color="gray" variant="light" radius="sm" tt="none">Conditions not met</Badge>
        )}
      </Group>
      {result.conditions_error && <Text size="sm" style={{ color: TOKENS.redText }}>{result.conditions_error}</Text>}
      {met === false && !result.conditions_error && (
        <Text size="sm" c="dimmed">{`This ${recordWord(v, result.record_type)} does not match, so no step would run.`}</Text>
      )}
      {result.goal && (
        <Group gap="xs" wrap="nowrap" data-testid="test-goal">
          <ThemeIcon variant="light" radius="xl" size={22} color="teal" style={{ flex: 'none' }}><IconFlag size={13} /></ThemeIcon>
          <Text size="sm">{result.goal}</Text>
        </Group>
      )}
      {result.steps.map((s) => (
        <Group
          key={s.step_id}
          gap="xs"
          wrap="nowrap"
          align="flex-start"
          data-testid="test-step"
          data-acts={s.acts ? 'yes' : 'no'}
          data-path={s.path ?? undefined}
          data-pending={s.pending ? 'yes' : undefined}
          style={{
            marginLeft: s.depth * 16,
            paddingLeft: s.depth ? 8 : 0,
            borderLeft: s.depth ? `2px solid ${TOKENS.border}` : undefined,
          }}
        >
          <ThemeIcon
            variant="light"
            radius="xl"
            size={22}
            color={s.pending ? 'chartAmber' : s.acts ? 'chartGreen' : 'gray'}
            style={{ flex: 'none', marginTop: 1 }}
            aria-label={s.pending ? 'Would wait' : s.acts ? 'Would act' : 'Would skip'}
          >
            {s.pending ? <IconHourglass size={13} /> : s.acts ? <IconCheck size={13} /> : <IconPlayerSkipForward size={13} />}
          </ThemeIcon>
          <div style={{ minWidth: 0 }}>
            <Group gap={6} wrap="wrap">
              <Text size="xs" c="dimmed">{stepLabel(v, s.kind)}</Text>
              {s.path && (
                <Badge size="xs" variant="light" radius="sm" tt="none" color={s.path === 'yes' ? 'chartGreen' : 'gray'}>
                  {s.path === 'yes' ? 'Yes path' : 'No path'}
                </Badge>
              )}
              {s.pending && <Badge size="xs" variant="light" radius="sm" tt="none" color="chartAmber">Pending</Badge>}
            </Group>
            <Text size="sm" style={{ overflowWrap: 'anywhere', color: s.acts ? TOKENS.text : TOKENS.textSecondary }}>{s.would}</Text>
          </div>
        </Group>
      ))}
      <Text size="sm" fw={500}>Nothing was changed.</Text>
    </Stack>
  );
}
